import { Component } from '@theme/component';
import {
  calculateHeaderGroupHeight,
  calculateHeaderTopOffset,
  changeMetaThemeColor,
  onDocumentLoaded,
  setHeaderMenuStyle,
  updateHeaderViewportBottom
} from '@theme/utilities';

// Eager-register *-component hosts for delegated `on:click="/open"`. Use relative paths so the
// production build inlines them; `@theme/*` is externalized and only works for importmap entries.
import './cart-drawer.js';
import './account-drawer.js';
import './cart-icon.js';
import './header-actions.js';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached first visible full-bleed section for near-top transparent checks.
   * @type {HTMLElement | null}
   */
  #firstTransparentSection = null;

  /**
   * Media queries that affect which section is the first visible / transparent hero.
   * @type {MediaQueryList[]}
   */
  #transparentMediaQueries = [];

  /**
   * Re-syncs transparency when main section order/markup changes (morph nav).
   * @type {MutationObserver | null}
   */
  #mainSectionObserver = null;

  /**
   * Re-syncs when section visibility / bleed markers change in place (theme editor).
   * @type {MutationObserver | null}
   */
  #sectionVisibilityObserver = null;

  /**
   * The last recorded scrollTop of the document, used by `scroll-up` and
   * `always` sticky modes to derive scroll direction.
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Scroll position captured when a drawer asks the header to remain visible.
   * The header should stay revealed until the buyer scrolls down from here.
   * @type {number | null}
   */
  #revealUntilScrollDownTop = null;

  /**
   * Cached header bar height (px). Updated on resize; reused on scroll to avoid layout thrash.
   * @type {number}
   */
  #cachedHeaderHeight = 0;

  /**
   * Cached header-group height (px).
   * @type {number}
   */
  #cachedHeaderGroupHeight = 0;

  /**
   * Cached announcement / pre-header offset (px).
   * @type {number}
   */
  #cachedHeaderTopOffset = 0;

  /**
   * Measure header geometry, cache it, then write CSS variables — reads before writes
   * so we do not force a reflow after invalidating styles.
   * @param {ResizeObserverEntry} [entry]
   */
  #publishHeaderMetrics = (entry) => {
    const headerGroup = document.querySelector('#header-group');
    const borderBox = entry?.borderBoxSize?.length > 0 ? entry.borderBoxSize[0]?.blockSize : undefined;
    // Prefer ResizeObserver's box when valid; fall back to offsetHeight for hosts that
    // report an empty border box before layout settles.
    const roundedHeaderHeight = Math.round(borderBox && borderBox > 0 ? borderBox : this.offsetHeight);
    if (!roundedHeaderHeight) return;

    const headerGroupHeight = Math.round(calculateHeaderGroupHeight(this, headerGroup));
    const headerTopOffset = calculateHeaderTopOffset(this, headerGroup);

    this.#cachedHeaderHeight = roundedHeaderHeight;
    this.#cachedHeaderGroupHeight = headerGroupHeight;
    this.#cachedHeaderTopOffset = headerTopOffset;

    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
    document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
    updateHeaderViewportBottom(this, headerGroup, {
      headerHeight: roundedHeaderHeight,
      headerTopOffset
    });
  };

  /**
   * Apply `--header-top-offset` / `--header-viewport-bottom` from cache after sticky
   * attribute changes — no geometry reads (sticky transforms would force reflow).
   */
  #applyCachedViewportBottom = () => {
    if (!this.#cachedHeaderHeight) {
      this.#publishHeaderMetrics();
      return;
    }

    updateHeaderViewportBottom(this, document.querySelector('#header-group'), {
      headerHeight: this.#cachedHeaderHeight,
      headerTopOffset: this.#cachedHeaderTopOffset
    });
  };

  /**
   * Re-measure and publish header CSS variables (used when announcement-bar
   * siblings resize outside this element's ResizeObserver).
   */
  refreshMetrics() {
    this.#publishHeaderMetrics();
  }

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    this.#publishHeaderMetrics(entry);
    this.#syncFirstSectionTransparent();

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to drive
   * `data-sticky-state` for `sticky="always"`. (`scroll-up` mode derives
   * its state from the scroll handler instead, so it doesn't need this.)
   */
  #observeStickyPosition = () => {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        this.dataset.stickyState = entry.isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      },
      { threshold: 1 }
    );

    this.#intersectionObserver.observe(this);
  };

  /**
   * Resolve whether the first *visible* main section is a full-bleed hero, then
   * sync near-top transparency (scroll position — not the whole bleed height).
   */
  #syncFirstSectionTransparent = () => {
    this.#syncTransparentForeground();
    this.#firstTransparentSection = this.#getFirstVisibleTransparentSection();

    if (!this.#firstTransparentSection) {
      this.removeAttribute('data-first-section-transparent');
      this.removeAttribute('data-over-transparent-section');
      return;
    }

    this.setAttribute('data-first-section-transparent', '');
    this.#syncTransparentNearTop();
  };

  /**
   * Header morph skips attribute updates (`data-skip-node-update`). Re-match
   * the white-chrome template lists against the current page template.
   */
  #syncTransparentForeground = () => {
    const desktopList = this.getAttribute('data-white-templates-desktop');
    const mobileList = this.getAttribute('data-white-templates-mobile');
    if (desktopList === null && mobileList === null) return;

    const rawTemplate = document.querySelector('.content-for-layout')?.getAttribute('data-template') || '';
    const handle = rawTemplate
      .replace(/\.json$/i, '')
      .toLowerCase()
      .trim();
    const base = handle.split('.')[0] || handle;

    const listIncludesTemplate = (list) => {
      const normalized = `,${(list || '').toLowerCase().replace(/\s+/g, '')},`;
      if (!list || normalized === ',,') return false;
      if (handle && normalized.includes(`,${handle},`)) return true;
      if (base && normalized.includes(`,${base},`)) return true;
      return false;
    };

    if (listIncludesTemplate(desktopList)) {
      this.setAttribute('data-transparent-fg-desktop', 'white');
    } else {
      this.removeAttribute('data-transparent-fg-desktop');
    }

    if (listIncludesTemplate(mobileList)) {
      this.setAttribute('data-transparent-fg-mobile', 'white');
    } else {
      this.removeAttribute('data-transparent-fg-mobile');
    }
  };

  /**
   * Transparent only near the page top over a full-bleed first section — not
   * across the whole bleed on scroll-up. Placement of that section is CSS-only.
   * Uses cached header heights so scroll frames do not remeasure layout.
   */
  #syncTransparentNearTop = () => {
    if (!this.#firstTransparentSection) {
      this.removeAttribute('data-over-transparent-section');
      return;
    }

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const nearTopLimit = Math.max(this.#cachedHeaderGroupHeight, this.#cachedHeaderHeight, 1);
    this.toggleAttribute('data-over-transparent-section', scrollTop <= nearTopLimit);
  };

  /**
   * @param {HTMLElement} section
   * @returns {boolean}
   */
  #isMainSectionDisplayed(section) {
    if (typeof section.checkVisibility === 'function') {
      return section.checkVisibility();
    }

    return getComputedStyle(section).display !== 'none';
  }

  /**
   * @param {HTMLElement} item
   * @returns {boolean}
   */
  #isMediaModuleItemDisplayed(item) {
    return this.#isMainSectionDisplayed(item);
  }

  /**
   * First displayed Media module block in a viewport-visible section.
   *
   * @param {Element} mediaModule
   * @returns {HTMLElement | null}
   */
  #getFirstVisibleMediaModuleItem(mediaModule) {
    const items = mediaModule.querySelectorAll('[data-media-module-item]');

    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;
      if (!this.#isMediaModuleItemDisplayed(item)) continue;

      return item;
    }

    return null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getFirstVisibleTransparentSection() {
    const main = document.querySelector('.content-for-layout');
    if (!(main instanceof HTMLElement)) return null;

    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    const sections = main.querySelectorAll(':scope > .shopify-section');

    for (const section of sections) {
      if (!(section instanceof HTMLElement)) continue;
      if (!this.#isMainSectionDisplayed(section)) continue;

      const mediaModule = section.querySelector('[data-media-module]');
      if (mediaModule instanceof Element) {
        const firstItem = this.#getFirstVisibleMediaModuleItem(mediaModule);
        if (!firstItem) continue;

        const marker = firstItem.querySelector('[data-transparent-header]');
        if (!(marker instanceof Element) || !this.#isTransparentHeaderActive(marker)) return null;

        return section;
      }

      const visibilityMarker =
        section.querySelector('[data-section-visibility]') || section.querySelector('[data-transparent-header]');
      const visibility = visibilityMarker?.getAttribute('data-section-visibility');
      if (visibility === 'desktop-only' && !isDesktop) continue;
      if (visibility === 'mobile-only' && isDesktop) continue;

      const marker = section.querySelector('[data-transparent-header]');
      if (!(marker instanceof Element) || !this.#isTransparentHeaderActive(marker)) return null;

      return section;
    }

    return null;
  }

  /**
   * @param {Element} element
   * @returns {boolean}
   */
  #isTransparentHeaderActive(element) {
    const mode = element.getAttribute('data-transparent-header');
    const visibility = element.getAttribute('data-section-visibility');

    if (mode === 'always') {
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
      if (visibility === 'desktop-only' && !isDesktop) return false;
      if (visibility === 'mobile-only' && isDesktop) return false;
      return true;
    }

    if (mode === 'desktop') {
      return window.matchMedia('(min-width: 1367px), (min-width: 750px) and (hover: hover) and (pointer: fine)')
        .matches;
    }

    return false;
  }

  #observeFirstSectionTransparent = () => {
    this.#syncFirstSectionTransparent();
    this.#observeMainSectionChanges();
    window.addEventListener('pageshow', this.#syncFirstSectionTransparent);

    if (this.#transparentMediaQueries.length) return;

    this.#transparentMediaQueries = [
      window.matchMedia('(min-width: 1024px)'),
      window.matchMedia('(min-width: 1367px)'),
      window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)')
    ];

    for (const mediaQuery of this.#transparentMediaQueries) {
      mediaQuery.addEventListener('change', this.#syncFirstSectionTransparent);
    }
  };

  /**
   * Header is skipped on morph (`data-skip-node-update`). Watch main so a
   * passepartout-first page does not keep homepage bleed transparency attrs.
   */
  #observeMainSectionChanges = () => {
    if (this.#mainSectionObserver) return;

    const main = document.querySelector('.content-for-layout');
    if (!(main instanceof HTMLElement)) return;

    this.#mainSectionObserver = new MutationObserver(() => {
      this.#syncFirstSectionTransparent();
    });
    this.#mainSectionObserver.observe(main, { childList: true });

    this.#sectionVisibilityObserver = new MutationObserver(() => {
      this.#syncFirstSectionTransparent();
    });
    this.#sectionVisibilityObserver.observe(main, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-section-visibility', 'data-transparent-header']
    });

    document.addEventListener('shopify:section:load', this.#syncFirstSectionTransparent);
    document.addEventListener('shopify:section:unload', this.#syncFirstSectionTransparent);
    document.addEventListener('shopify:section:reorder', this.#syncFirstSectionTransparent);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;

      // Gather layout reads before sticky attribute writes invalidate style.
      const stickyMode = this.getAttribute('sticky');
      const scrollTop = document.scrollingElement?.scrollTop ?? 0;
      const headerTop = stickyMode === 'always' ? this.getBoundingClientRect().top : 0;

      this.#updateScrollState(scrollTop, headerTop);
      this.#syncTransparentNearTop();
      this.#applyCachedViewportBottom();
    });
  };

  #revealUntilScrollDown = (event) => {
    if (this.getAttribute('sticky') !== 'scroll-up') return;

    const eventScrollY = event instanceof CustomEvent ? Number(event.detail?.scrollY) : Number.NaN;
    const scrollTop = Number.isFinite(eventScrollY) ? eventScrollY : (document.scrollingElement?.scrollTop ?? 0);
    const isPendingScrollRestore =
      event instanceof CustomEvent && event.detail && event.detail.pendingScrollRestore === true;

    // At the page top the header is already visible. Latching reveal here zeros
    // `--bleed-underlap` and makes a transparent full-bleed header look solid.
    if (!isPendingScrollRestore && scrollTop <= 0) {
      this.#revealUntilScrollDownTop = null;
      this.removeAttribute('data-reveal-until-scroll-down');
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
      this.#lastScrollTop = scrollTop;
      this.#syncTransparentNearTop();
      this.#applyCachedViewportBottom();
      return;
    }

    this.#revealUntilScrollDownTop = isPendingScrollRestore ? Number.POSITIVE_INFINITY : scrollTop;
    this.setAttribute('data-reveal-until-scroll-down', '');
    this.dataset.stickyState = scrollTop <= 0 ? 'inactive' : 'active';
    this.dataset.scrollDirection = scrollTop <= 0 ? 'none' : 'up';
    this.#lastScrollTop = scrollTop;
    this.#syncTransparentNearTop();
    this.#applyCachedViewportBottom();
  };

  /**
   * @param {number} [scrollTop]
   * @param {number} [headerTop]
   */
  #updateScrollState = (scrollTop = document.scrollingElement?.scrollTop ?? 0, headerTop = 0) => {
    const stickyMode = this.getAttribute('sticky');
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    // `scroll-up` mode: hide the header while scrolling down, reveal it
    // while scrolling up. Drives `data-sticky-state` directly off the
    // document scroll position so it works even when the header is
    // `position: fixed` from page load (in which case the
    // IntersectionObserver below would never report it as offscreen).
    if (stickyMode === 'scroll-up') {
      const isAtTop = scrollTop <= 0;
      const scrollingEl = document.scrollingElement;
      const maxScrollTop = scrollingEl ? scrollingEl.scrollHeight - scrollingEl.clientHeight : 0;
      const isAtBottom = maxScrollTop > 0 && scrollTop >= maxScrollTop - 1;

      if (this.#revealUntilScrollDownTop !== null) {
        if (this.#revealUntilScrollDownTop === Number.POSITIVE_INFINITY) {
          this.dataset.stickyState = scrollTop <= 0 ? 'inactive' : 'active';
          this.dataset.scrollDirection = scrollTop <= 0 ? 'none' : 'up';
          this.#lastScrollTop = scrollTop;

          // Restore landed at top — drop the latch so bleed underlap returns.
          if (scrollTop <= 0) {
            this.#revealUntilScrollDownTop = null;
            this.removeAttribute('data-reveal-until-scroll-down');
          }
          return;
        }

        if (isAtTop) {
          this.#revealUntilScrollDownTop = null;
          this.removeAttribute('data-reveal-until-scroll-down');
          this.dataset.stickyState = 'inactive';
          this.dataset.scrollDirection = 'none';
          this.#lastScrollTop = scrollTop;
          return;
        }

        if (scrollTop <= this.#revealUntilScrollDownTop) {
          this.dataset.stickyState = 'active';
          this.dataset.scrollDirection = 'up';
          this.#lastScrollTop = scrollTop;
          return;
        }

        this.#revealUntilScrollDownTop = null;
        this.removeAttribute('data-reveal-until-scroll-down');
        this.dataset.stickyState = 'idle';
        this.dataset.scrollDirection = 'down';
        this.#lastScrollTop = scrollTop;
        return;
      }

      if (isAtTop) {
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp && !isAtBottom) {
        // Don't reveal at the bottom — the layout shift from showing the header
        // causes the scroll position to decrease, which looks like "scrolling up"
        // and creates a bounce feedback loop.
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.stickyState = 'idle';
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    // `always` mode: header is always sticky; only the scroll direction is
    // exposed. Sticky state is managed by the IntersectionObserver.
    if (stickyMode === 'always') {
      const isAtTop = headerTop >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    // Seed height cache before scroll handlers run (ResizeObserver is async).
    this.#publishHeaderMetrics();

    // Resolve transparency before sticky-state so CSS never flashes solid white
    // between those attribute writes on a full-bleed first section.
    this.#observeFirstSectionTransparent();

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode === 'always') {
      this.#observeStickyPosition();
    }
    if (stickyMode === 'scroll-up' || stickyMode === 'always') {
      document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      const scrollTop = document.scrollingElement?.scrollTop ?? 0;
      this.#lastScrollTop = scrollTop;

      // Seed from current scroll so a mid-page refresh is not treated as
      // "no sticky state" (CSS maps that to transparent over bleed heroes).
      // Use active (revealed) when restored mid-page — not idle, or the bar hides.
      if (scrollTop <= 0) {
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else if (stickyMode === 'scroll-up') {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }

      this.#syncTransparentNearTop();
    }
    this.addEventListener('revealUntilScrollDown', this.#revealUntilScrollDown);
    document.addEventListener('scrollLockReleased', this.#revealUntilScrollDown);

    // Enable bg fades only after the initial transparent/solid state is settled.
    if (!document.documentElement.hasAttribute('data-header-bg-ready')) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.documentElement.setAttribute('data-header-bg-ready', '');
        });
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#mainSectionObserver?.disconnect();
    this.#mainSectionObserver = null;
    this.#sectionVisibilityObserver?.disconnect();
    this.#sectionVisibilityObserver = null;
    window.removeEventListener('pageshow', this.#syncFirstSectionTransparent);
    document.removeEventListener('shopify:section:load', this.#syncFirstSectionTransparent);
    document.removeEventListener('shopify:section:unload', this.#syncFirstSectionTransparent);
    document.removeEventListener('shopify:section:reorder', this.#syncFirstSectionTransparent);
    for (const mediaQuery of this.#transparentMediaQueries) {
      mediaQuery.removeEventListener('change', this.#syncFirstSectionTransparent);
    }
    this.#transparentMediaQueries = [];
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.removeEventListener('revealUntilScrollDown', this.#revealUntilScrollDown);
    document.removeEventListener('scrollLockReleased', this.#revealUntilScrollDown);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => {
      if (header instanceof HeaderComponent) {
        header.refreshMetrics();
        return;
      }

      if (!(header instanceof HTMLElement)) return;

      // Reads first, then writes — same batching as HeaderComponent.#publishHeaderMetrics.
      const headerHeight = Math.round(header.offsetHeight);
      const headerGroupHeight = Math.round(calculateHeaderGroupHeight(header, headerGroup));
      const headerTopOffset = calculateHeaderTopOffset(header, headerGroup);

      document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
      updateHeaderViewportBottom(header, headerGroup, { headerHeight, headerTopOffset });
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    const observeHeaderGroupChildren = () => {
      const children = headerGroup.children;

      for (let i = 0; i < children.length; i++) {
        const element = children[i];
        if (element instanceof HTMLElement) {
          resizeObserver.observe(element);
        }
      }
    };

    observeHeaderGroupChildren();

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          observeHeaderGroupChildren();
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
